import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useLocation, Navigate, Link, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Menu, 
  MenuItem,
  Avatar,
  Divider,
  Tooltip,
  Zoom,
  useMediaQuery,
  GlobalStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  Button
} from '@mui/material';
import { ThemeProvider, alpha } from '@mui/material/styles';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import DebugApiCache from './components/DebugApiCache';
// Removed unused: BackupIcon, HistoryIcon, VersionsIcon
import DashboardIcon from '@mui/icons-material/Dashboard';
import CampaignIcon from '@mui/icons-material/Campaign';
import BarChartIcon from '@mui/icons-material/BarChart';
// Removed unused: ListAltIcon, TableChartIcon

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { AnimatePresence } from 'framer-motion';
import PageTransition from './components/PageTransition';
import { NotificationProvider, useNotification } from './context/NotificationContext';
import AnimatedLoader from './components/AnimatedLoader';
import NotificationBell from './components/NotificationBell';
import axios from 'axios';
import { campaignApi } from './services/api';

// Pages
import DashboardRouter from './pages/DashboardRouter';
import CampaignsRouter from './pages/CampaignsRouter';
import ExcelLikeView from './pages/ExcelLikeView';
import ExcelViewRouter from './pages/ExcelViewRouter';
// Removed unused: CampaignsSnapshotExcel
// Removed: DashboardSnapshot - functionality merged into CurrentDashboard
import TotalData from './pages/TotalData';
import Login from './pages/Login';
import Settings from './pages/Settings';
import ATLManageData from './pages/ATLManageData';
import WrenEmbedPage from './pages/WrenEmbedPage';
// import Reports from './pages/Reports';

import AuditLogs from './pages/RestoreLog';
import VersionManagement from './pages/VersionManagement';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';
import { UserProvider, useUser } from './context/UserContext';

// Permission components
import ProtectedRoute from './components/ProtectedRoute';
// Removed unused: PermissionWrapper
import usePermissions from './hooks/usePermissions';

// Import premium theme
import premiumTheme from './theme/premiumTheme';

// Security: Console protection (similar to Facebook's Self-XSS protection)
import './utils/consoleProtection';

// Create a theme
const theme = premiumTheme('light');

// AppBar Breadcrumb Component
const AppBarBreadcrumb = () => {
  const location = useLocation();
  const [campaignData, setCampaignData] = useState(null);
  
  // Extract route parts and query params
  const pathParts = location.pathname.split('/').filter(Boolean);
  const params = new URLSearchParams(location.search);
  const snapshotMonth = params.get('snapshotMonth');
  
  // Check if this is an excel-view route with campaign ID
  useEffect(() => {
    const fetchCampaignName = async () => {
      if (pathParts[0] === 'excel-view' && pathParts[1]) {
        try {
          const campaignId = pathParts[1];
          
          if (snapshotMonth) {
            // For snapshot data, fetch from snapshot API
            const response = await campaignApi.getCampaignsBySnapshot(snapshotMonth);
            let campaignFromSnapshot = null;
            
            // Handle different response formats from the snapshot API
            if (response.data.results) {
              campaignFromSnapshot = response.data.results.find(
                item => item.id.toString() === campaignId.toString()
              );
            } else if (response.data.snapshot?.campaigns) {
              campaignFromSnapshot = response.data.snapshot.campaigns.find(
                item => item.id.toString() === campaignId.toString()
              );
            } else if (Array.isArray(response.data)) {
              campaignFromSnapshot = response.data.find(
                item => item.id.toString() === campaignId.toString()
              );
            }
            
            if (campaignFromSnapshot) {
              setCampaignData({
                ...campaignFromSnapshot,
                campaign_name: campaignFromSnapshot.campaign_name || campaignFromSnapshot.name,
                brand_name: campaignFromSnapshot.brand_name || campaignFromSnapshot.brand
              });
            } else {
              setCampaignData(null);
            }
          } else {
            // For live data, fetch from regular API
            const response = await campaignApi.getCampaign(campaignId);
            setCampaignData(response.data);
          }
        } catch (error) {
          console.error('Error fetching campaign for breadcrumb:', error);
          setCampaignData(null);
        }
      } else {
        setCampaignData(null);
      }
    };

    // Add timeout to prevent hanging requests
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Request timeout')), 5000)
    );

    Promise.race([fetchCampaignName(), timeoutPromise]).catch(error => {
      console.error('Campaign fetch timeout or error:', error);
      setCampaignData(null);
    });
  }, [location.pathname, location.search]);

  // Build breadcrumb
  if (pathParts[0] === 'excel-view') {
    const parts = ['excel-view'];
    
    if (campaignData) {
      // Add brand name if available
      if (campaignData.brand_name) {
        parts.push(campaignData.brand_name);
      }
      // Add campaign name
      parts.push(campaignData.campaign_name || campaignData.name || `Campaign ${pathParts[1]}`);
      // Add media type if available
      if (pathParts[2]) {
        parts.push(pathParts[2]);
      }
      // Add snapshot indicator if viewing snapshot
      if (snapshotMonth) {
        parts.push(`(${snapshotMonth})`);
      }
    } else if (pathParts[1]) {
      // Fallback: show loading or default
      parts.push('Loading...');
      if (pathParts[2]) {
        parts.push(pathParts[2]);
      }
      if (snapshotMonth) {
        parts.push(`(${snapshotMonth})`);
      }
    }
    
    return parts.join(' / ');
  }
  
  // Default breadcrumb for other routes
  return pathParts.join(' / ') || 'Home';
};

// Loading indicator component
const LoadingScreen = () => (
  <AnimatedLoader loading={true} message="Loading application..." />
);

// Basic auth protected route component
const AuthProtectedRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  if (!currentUser) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

// Main navigation component that requires authentication
const MainNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { user } = useUser();
  const { navigationMenu, hasPermission, loading: permLoading } = usePermissions();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const isUserMenuOpen = Boolean(userMenuAnchor);
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  
  // Drawer width depending on open state
  const drawerWidth = 240;
  const closedDrawerWidth = 72;
  const actualDrawerWidth = drawerOpen ? drawerWidth : closedDrawerWidth;

  // Handle window resize
  useEffect(() => {
    if (isMobile) {
      setDrawerOpen(false);
    }
  }, [isMobile]);
  
  // Toggle drawer
  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };
  
  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };
  
  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };
  
  const handleLogout = async () => {
    handleUserMenuClose();
    await logout();
  };

  const handleNavigateToSettings = () => {
    console.log('Settings permissions check:', {
      'manage_system_settings': hasPermission('manage_system_settings'),
      'manage_categories': hasPermission('manage_categories'),
      'manage_users': hasPermission('manage_users'),
      'all_permissions': Object.keys(navigationMenu?.permissions || {}),
      'user_role': user?.role
    });
    navigate('/settings');
    handleUserMenuClose();
  };


  // Permission-based navigation items
  const getMenuItems = () => {
    if (permLoading || !navigationMenu) {
      return [];
    }

    const menuItems = [];

    // Dashboard
    if (hasPermission('view_dashboard')) {
      menuItems.push({ 
        text: 'Dashboard', 
        icon: <DashboardIcon />, 
        path: '/',
        permission: 'view_dashboard'
      });
    }

    // Campaigns
    if (hasPermission('view_campaigns')) {
      menuItems.push({ 
        text: 'Campaigns', 
        icon: <CampaignIcon />, 
        path: '/campaigns',
        permission: 'view_campaigns'
      });
    }

    // ATL Manage Data
    menuItems.push({
      text: 'ATL Manage Data',
      icon: <BarChartIcon />,
      path: '/atl',
      permission: 'view_campaigns'
    });

    // Agent (Wren AI)
    menuItems.push({
      text: 'Agent',
      icon: <BarChartIcon />,
      path: '/agent',
      permission: 'view_campaigns'
    });


      // Removed: Custom Analytics Report and Dashboard Builder from menu

    // Excel View / Accruals - HIDDEN
    // if (hasPermission('view_accruals')) {
    //   menuItems.push({ 
    //     text: 'Heineken Report', 
    //     icon: <BarChartIcon />, 
    //     path: '/excel-view',
    //     permission: 'view_accruals'
    //   });
    // }

    // Total Data - comprehensive view - HIDDEN
    // if (hasPermission('view_accruals')) {
    //   menuItems.push({ 
    //     text: 'Total Data', 
    //     icon: <TableChartIcon />, 
    //     path: '/total-data',
    //     permission: 'view_accruals'
    //   });
    // }

    // Settings (available based on permissions) - HIDDEN
    // if (hasPermission('manage_system_settings') || hasPermission('manage_categories') || hasPermission('manage_users')) {
    //   menuItems.push({ 
    //     text: 'Settings', 
    //     icon: <SettingsIcon />, 
    //     path: '/settings',
    //     permission: 'manage_system_settings'
    //   });
    // }

    return menuItems;
  };
  
  return (
    <Box sx={{ 
      display: 'flex', 
      width: '100%', 
      minHeight: '100vh',
      overflow: 'hidden'
    }}>
      {/* Sidebar Drawer */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={drawerOpen}
        onClose={toggleDrawer}
        sx={{
          width: actualDrawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { 
            width: actualDrawerWidth, 
            boxSizing: 'border-box', 
            backgroundColor: '#fff', 
            color: '#000', 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'space-between',
            boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)'
          },
        }}
      >
        <Box sx={{ overflow: 'auto', mt: 2 }}>
          <Box sx={{ px: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: drawerOpen ? 'space-between' : 'center' }}>
            {drawerOpen && (
              <Typography variant="h6" sx={{ opacity: drawerOpen ? 1 : 0, transition: 'opacity 0.3s', color: '#000' }}>
                Accrual
              </Typography>
            )}
            <IconButton color="inherit" onClick={toggleDrawer} size="small">
              {drawerOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2, backgroundColor: alpha('#000', 0.1) }} />
          
          {/* User Info */}
          {drawerOpen && currentUser && (
            <Box sx={{ 
              px: 2, 
              mb: 2,
              backgroundColor: alpha('#1976d2', 0.05),
              borderRadius: 2,
              mx: 1,
              py: 1.5
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5,
                p: 1,
                borderRadius: 1.5,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                border: '1px solid',
                borderColor: alpha('#1976d2', 0.1)
              }}>
                <Avatar sx={{ 
                  width: 40, 
                  height: 40, 
                  backgroundColor: '#1976d2',
                  fontSize: '1.1rem',
                  fontWeight: 600
                }}>
                  {currentUser.email?.charAt(0).toUpperCase() || 'U'}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 600, 
                      color: '#1976d2',
                      fontSize: '0.875rem',
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {currentUser.email || 'User'}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: alpha('#000', 0.6),
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      textTransform: 'capitalize'
                    }}
                  >
                    {currentUser.role_name || currentUser.role || 'No Role'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
          <Divider sx={{ mb: 2, backgroundColor: alpha('#000', 0.1) }} />
          
          {/* Menu Items */}
          <List>
            {getMenuItems().map((item, index) => (
              item.divider ? (
                <Divider key={`divider-${index}`} sx={{ my: 1, backgroundColor: alpha('#000', 0.1) }} />
              ) : (
                <ListItem key={item.text || `item-${index}`} disablePadding sx={{ display: 'block', mb: 0.5 }}>
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      justifyContent: drawerOpen ? 'initial' : 'center',
                      px: 2.5,
                      backgroundColor: location.pathname === item.path ? alpha('#000', 0.1) : 'transparent',
                      '&:hover': {
                        backgroundColor: alpha('#000', 0.15),
                      },
                    }}
                    {...(item.onClick ? { onClick: item.onClick } : { component: Link, to: item.path })}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: drawerOpen ? 2 : 'auto',
                        justifyContent: 'center',
                        color: location.pathname === item.path ? '#000' : alpha('#000', 0.7),
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.text} sx={{ opacity: drawerOpen ? 1 : 0, color: '#000' }} />
                  </ListItemButton>
                </ListItem>
              )
            ))}
          </List>
        </Box>
        
        {/* Bottom Section with Settings and Logout */}
        <Box sx={{ p: 2, borderTop: `1px solid ${alpha('#000', 0.1)}` }}>
          <List>
            {/* Settings button - always show for now to debug */}
            {true && (
              <ListItem disablePadding sx={{ display: 'block', mb: 1 }}>
                <ListItemButton
                  sx={{
                    minHeight: 40,
                    justifyContent: drawerOpen ? 'initial' : 'center',
                    px: 2,
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: alpha('#000', 0.15),
                    },
                  }}
                  onClick={handleNavigateToSettings}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: drawerOpen ? 2 : 'auto',
                      justifyContent: 'center',
                      color: alpha('#000', 0.7),
                    }}
                  >
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Settings" sx={{ opacity: drawerOpen ? 1 : 0, color: '#000' }} />
                </ListItemButton>
              </ListItem>
            )}
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                sx={{
                  minHeight: 40,
                  justifyContent: drawerOpen ? 'initial' : 'center',
                  px: 2,
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: alpha('#d32f2f', 0.15),
                  },
                }}
                onClick={handleLogout}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: drawerOpen ? 2 : 'auto',
                    justifyContent: 'center',
                    color: '#d32f2f',
                  }}
                >
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Logout" sx={{ opacity: drawerOpen ? 1 : 0, color: '#d32f2f' }} />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>
      
      {/* Main Content Area */}
      <Box component="main" sx={{ 
        flexGrow: 1, 
        width: isMobile ? '100%' : `calc(100% - ${actualDrawerWidth}px)`,
        backgroundColor: '#f8fafc', 
        minHeight: '100vh',
        ml: isMobile ? 0 : 0,
        transition: 'width 0.3s ease, margin-left 0.3s ease',
        overflow: 'hidden'
      }}>
        {/* AppBar */}
        {location.pathname !== '/agent' && (
          <AppBar 
            position="fixed" 
            elevation={0}
            sx={{ 
              zIndex: (theme) => theme.zIndex.drawer + 1, 
              backgroundColor: '#fff', 
              color: '#333',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)',
              width: isMobile ? '100%' : `calc(100% - ${actualDrawerWidth}px)`,
              transition: 'width 0.3s ease'
            }}
          >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={toggleDrawer}
              edge="start"
              sx={{
                marginRight: 5,
              }}
            >
              {drawerOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
              <AppBarBreadcrumb />
            </Typography>
            <NotificationBell />
            <Tooltip title={currentUser?.email || 'User'} arrow TransitionComponent={Zoom}>
              <IconButton onClick={handleUserMenuOpen} color="inherit">
                <Avatar sx={{ width: 32, height: 32, mr: 1, backgroundColor: '#1976d2' }}>
                  {currentUser?.email?.charAt(0).toUpperCase() || 'U'}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={userMenuAnchor}
              open={isUserMenuOpen}
              onClose={handleUserMenuClose}
              onClick={handleUserMenuClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 1.5,
                  '& .MuiAvatar-root': {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              {/* Settings menu item - only show if user has permissions */}
              {(hasPermission('manage_system_settings') || hasPermission('manage_categories') || hasPermission('manage_users')) && (
                <MenuItem onClick={handleNavigateToSettings}>
                  <ListItemIcon>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  Settings
                </MenuItem>
              )}
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>
        )}
        
        {/* Main content with padding */}
        <Box sx={{ 
          mt: location.pathname === '/agent' ? 0 : 10, 
          p: location.pathname === '/agent' ? 0 : 3, 
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
          boxSizing: 'border-box',
          position: location.pathname === '/agent' ? 'relative' : 'static',
          height: location.pathname === '/agent' ? '100vh' : 'auto'
        }}>
          <MainContent />
        </Box>
      </Box>

    </Box>
  );
};

// Main content box with routes
const MainContent = () => {
  const location = useLocation();
  const notifications = useNotification();
  
  // Expose notification functions globally for API error handling
  useEffect(() => {
    // Expose showToast method globally for API error handling
    window.showToast = ({ type, title, message }) => {
      switch (type) {
        case 'success':
          notifications.showSuccess(message || title);
          break;
        case 'error':
          notifications.showError(message || title);
          break;
        case 'warning':
          notifications.showWarning(message || title);
          break;
        case 'info':
        default:
          notifications.showInfo(message || title);
      }
    };

    return () => {
      // Clean up global reference when component unmounts
      delete window.showToast;
    };
  }, [notifications]);

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
                {/* Dashboard - Protected with view_dashboard permission */}
        <Route path="/" element={
          <ProtectedRoute requiredPermission="view_dashboard">
            <PageTransition>
              <DashboardRouter />
            </PageTransition>
          </ProtectedRoute>
        } />
        
        {/* Dashboard alias - Redirect /dashboard to / */}
        <Route path="/dashboard" element={<Navigate to="/" replace />} />
        
        {/* Legacy Snapshot Dashboard route - redirect to main dashboard */}
        <Route path="/dashboard-snapshot" element={
          <Navigate to="/" replace />
        } />
        
                      {/* Campaigns - Protected with view_campaigns permission */}
              <Route path="/campaigns" element={
                <ProtectedRoute requiredPermission="view_campaigns">
                  <PageTransition>
                    <CampaignsRouter />
                  </PageTransition>
                </ProtectedRoute>
              } />
        
        {/* Reports route removed */}

        {/* ATL Manage Data - reuse view_campaigns permission */}
        <Route path="/atl" element={
          <ProtectedRoute requiredPermission="view_campaigns">
            <PageTransition>
              <ATLManageData />
            </PageTransition>
          </ProtectedRoute>
        } />

        {/* Agent - Wren AI embedded from the Wren UI port */}
        <Route path="/agent" element={
          <ProtectedRoute requiredPermission="view_campaigns">
            <WrenEmbedPage />
          </ProtectedRoute>
        } />
        
        {/* Excel View / Accruals - Protected with view_accruals permission */}
        <Route path="/excel-view" element={
          <ProtectedRoute requiredPermission="view_accruals">
            <PageTransition>
              <ExcelLikeView />
            </PageTransition>
          </ProtectedRoute>
        } />
        <Route path="/excel-view/:campaignId/:mediaType" element={
          <ProtectedRoute requiredPermission="view_accruals">
            <PageTransition>
              <ExcelViewRouter />
            </PageTransition>
          </ProtectedRoute>
        } />
        
        {/* Total Data - Protected with view_accruals permission */}
        <Route path="/total-data" element={
          <ProtectedRoute requiredPermission="view_accruals">
            <PageTransition>
              <TotalData />
            </PageTransition>
          </ProtectedRoute>
        } />
        
        {/* Settings - Protected with system settings or category management permissions */}
        <Route path="/settings" element={
          <ProtectedRoute anyPermissions={['manage_system_settings', 'manage_categories', 'manage_users']}>
            <PageTransition>
              <Settings />
            </PageTransition>
          </ProtectedRoute>
        } />
        
        {/* Audit Logs - Protected with view_audit_logs permission */}
        <Route path="/audit-logs" element={
          <ProtectedRoute requiredPermission="view_audit_logs">
            <PageTransition>
              <AuditLogs />
            </PageTransition>
          </ProtectedRoute>
        } />
        
        {/* Version Management - Admin only */}
        <Route path="/versions" element={
          <ProtectedRoute requireAdmin={true}>
            <PageTransition>
              <VersionManagement />
            </PageTransition>
          </ProtectedRoute>
        } />
      </Routes>
    </AnimatePresence>
  );
};

function App() {
  const location = useLocation();
  const [previousPath, setPreviousPath] = useState(null);

  const logPageView = useCallback(async () => {
    const currentPath = location.pathname + location.search;
    console.log('Logging page view on mount or route change:', currentPath);
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      try {
        // Use window.AMS_CONFIG for production compatibility
        const BACKEND_API_URL = window.AMS_CONFIG?.API_URL || 'http://localhost:8000';
        const API_PATH = window.AMS_CONFIG?.API_PATH || '/api';
        const API_BASE_URL = `${BACKEND_API_URL}${API_PATH}`;
        const endpoints = [
          `${API_BASE_URL}/log-page-view/`
        ];
        await axios.post(endpoints[0], { page_name: currentPath }, {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        });
      } catch (error) {
        console.error('Error logging page view:', error);
      }
    }
  }, [location]);

  useEffect(() => {
    logPageView();
  }, [logPageView]);

  // Removed forced full-page reload on route change to prevent repeated API calls
  useEffect(() => {
    setPreviousPath(location.pathname);
  }, [location.pathname]);

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles
        styles={{
          'html, body, #root': {
            overflowX: 'hidden !important',
            maxWidth: '100vw !important',
            margin: 0,
            padding: 0,
          },
          '*': {
            boxSizing: 'border-box !important',
          },
          '.MuiContainer-root': {
            paddingLeft: '16px !important',
            paddingRight: '16px !important',
            maxWidth: '100% !important',
            width: '100% !important',
            margin: '0 auto !important',
          },
          '.MuiGrid-container': {
            width: '100% !important',
            margin: '0 !important',
          },
          '.MuiGrid-item': {
            maxWidth: '100% !important',
          },
          '.MuiPaper-root': {
            maxWidth: '100% !important',
          },
          '.MuiTableContainer-root': {
            maxWidth: '100% !important',
            overflowX: 'auto !important',
          },
          '.MuiBox-root': {
            maxWidth: '100% !important',
          },
          '.MuiTabs-root': {
            maxWidth: '100% !important',
          },
          '@media (max-width: 768px)': {
            '.MuiTable-root': {
              minWidth: '600px !important',
            }
          }
        }}
      />
      <NotificationProvider>
        <AuthProvider>
          <UserProvider>
            <AnimatePresence mode="wait">
              <Routes location={location} key={location.pathname}>
                {/* Public Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                
                {/* Protected Routes - Authentication Required */}
                <Route path="/*" element={
                  <AuthProtectedRoute>
                    <MainNavigation />
                  </AuthProtectedRoute>
                } />
              </Routes>
            </AnimatePresence>
          </UserProvider>
        </AuthProvider>
      </NotificationProvider>
      
      {/* Debug API Cache Component - Only in development */}
      <DebugApiCache />
    </ThemeProvider>
  );
}

export default App;
